import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { formatShortDate } from "../chiefComplaints/dateHelpers";
import { TrendDot, TrendTip, useTrendTip } from "../trendChart";
import { FLAG_BANDS, FLAG_CHART_BG, RANGE_AXIS, readingTag } from "./data";

/**
 * Categorical trend chart — Y axis is the Range (High / Medium / Normal /
 * Low / Abnormal / None), plotted top-to-bottom in that order. Each entry
 * lands on one of those horizontal rails; the polyline joins them across
 * time. Coloured horizontal bands sit behind each rail with the same
 * palette as the audit-table Range pills. Replaces the numeric
 * InvestigationChart on the "Range over time" trend modal.
 *
 * Kept intentionally minimal (no library, no interactivity beyond
 * <title> tooltips). Marked-incorrect entries are filtered out by the
 * caller — this component just draws what it's given.
 */
const W = 720;
const H = 260;
const PAD = { l: 70, r: 24, t: 20, b: 40 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

const rowY = (idx) => PAD.t + (IH / RANGE_AXIS.length) * (idx + 0.5);
const bandTop = (idx) => PAD.t + (IH / RANGE_AXIS.length) * idx;
const bandHeight = IH / RANGE_AXIS.length;

const RangeChart = ({ entries, unit }) => {
  const points = useMemo(() => {
    return (entries || [])
      .filter((e) => e && e.testDate)
      .map((e) => ({ ...e, _t: new Date(e.testDate).getTime() }))
      .filter((e) => !isNaN(e._t))
      .sort((a, b) => a._t - b._t);
  }, [entries]);

  // White HTML tooltip anchored on hover — replaces the browser's native
  // SVG <title> reveal (which paints itself using OS-defaults and can't
  // be styled). See useTrendTip in ../trendChart for the shared plumbing.
  const { wrapRef, tip, showTip, hideTip } = useTrendTip();

  if (points.length === 0) {
    return (
      <div className="cc-chart-empty">
        No entries to chart yet.
      </div>
    );
  }

  const minT = points[0]._t;
  // Guarantee a visible span even when every reading shares the same
  // TestDate (common for a freshly-added test). Mirrors the CC + Dx
  // trend charts' bump so the single column has width and the x-axis
  // labels at the left + right edges don't collapse onto each other.
  const maxT = Math.max(points[points.length - 1]._t, minT + 24 * 60 * 60 * 1000);
  const tRange = maxT - minT;

  const xFor = (t) => PAD.l + ((t - minT) / tRange) * IW;
  const idxOf = (flag) => {
    const i = RANGE_AXIS.indexOf(flag);
    return i < 0 ? RANGE_AXIS.length - 1 : i; // fall back to "none" rail
  };
  const yFor = (flag) => rowY(idxOf(flag));

  const polylinePoints = points.map((p) => `${xFor(p._t)},${yFor(p.flag)}`).join(" ");

  return (
    <div className="cc-chart li-chart-wrap" ref={wrapRef}>
    <svg className="li-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Range trend chart">
      {/* Horizontal Range bands. Rail fill uses the chart-specific pale
          palette (FLAG_CHART_BG) — kept independent of the table chips'
          `FLAG_BANDS.bg` values. Rendered at full opacity so the locked
          hex codes land pixel-accurate; the values themselves are pale
          enough that the polyline + dots read clearly on top. Text
          label colour still comes from FLAG_BANDS.fg. */}
      {RANGE_AXIS.map((key, i) => {
        const band = FLAG_BANDS[key];
        return (
          <g key={`band-${key}`}>
            <rect
              x={PAD.l}
              y={bandTop(i)}
              width={IW}
              height={bandHeight}
              fill={FLAG_CHART_BG[key] || band.bg}
            />
            <text
              x={PAD.l - 12}
              y={rowY(i) + 3}
              fontSize="11"
              fill={band.fg}
              textAnchor="end"
              fontWeight="600"
            >
              {band.label}
            </text>
          </g>
        );
      })}

      {/* X-axis baseline */}
      <line
        x1={PAD.l}
        x2={W - PAD.r}
        y1={PAD.t + IH}
        y2={PAD.t + IH}
        stroke="#d7dae2"
      />

      {/* Trend line */}
      <polyline
        points={polylinePoints}
        fill="none"
        stroke="#6a5ef5"
        strokeWidth="1.8"
      />

      {/* Points */}
      {points.map((p, i) => {
        // Per-point unit — an audit row can carry its own unit that
        // differs from the header's `unit` prop (e.g., corrected rows,
        // manually-digitised rows with a picked unit). Fall back to the
        // component-level `unit` when the row didn't record one.
        const pUnit = (p.test && p.test.unit) || unit || "";
        const flagLabel = p.flag && p.flag !== "unknown"
          ? (FLAG_BANDS[p.flag] ? FLAG_BANDS[p.flag].label : p.flag)
          : "";
        // The tag tells readings of one test apart ("Morning" vs "Evening"),
        // which all share this one line.
        const pTag = readingTag(p.test);
        const tipText = [
          `${formatShortDate(p.testDate)} · ${pTag ? `${pTag} · ` : ""}${p.value}${pUnit ? ` ${pUnit}` : ""}`,
          flagLabel ? ` · ${flagLabel}` : "",
        ].join("");
        return (
          <TrendDot
            key={`pt-${i}`}
            cx={xFor(p._t)}
            cy={yFor(p.flag)}
            scale={0.85}
            onMouseEnter={(e) => showTip(e, tipText)}
            onMouseMove={(e) => showTip(e, tipText)}
            onMouseLeave={hideTip}
          />
        );
      })}

      {/* No inline last-point flag annotation — the current range is
          already surfaced by (a) the "Medium/Low/…" pill next to the
          test name in the modal header, (b) the Y-axis rail label the
          dot sits on, and (c) the "Latest reported value: … · <range>"
          line directly beneath the chart. Reprinting it a fourth time
          on the polyline just added visual noise, especially when two
          points share a test date and the label floated over the
          vertical hop between them. */}

      {/* X-axis labels — FIRST date pinned to the plot area's left edge
          (text-anchor: start), LAST date pinned to the right edge
          (text-anchor: end). Matches the CC and Dx trend charts'
          endpoint style. The right label is suppressed when it would
          format to the same date as the left (single entry, OR multiple
          entries all on the same calendar day) — a duplicate "02 Oct
          2026" on both ends adds no information and reads as a bug.
          Intermediate ticks intentionally omitted per the "endpoints
          only" ask. */}
      {(() => {
        const firstLabel = formatShortDate(points[0].testDate);
        const lastLabel = formatShortDate(points[points.length - 1].testDate);
        return (
          <>
            <text
              x={PAD.l}
              y={H - 12}
              fontSize="10"
              fill="#6a7282"
              textAnchor="start"
            >
              {firstLabel}
            </text>
            {points.length > 1 && lastLabel !== firstLabel && (
              <text
                x={PAD.l + IW}
                y={H - 12}
                fontSize="10"
                fill="#6a7282"
                textAnchor="end"
              >
                {lastLabel}
              </text>
            )}
          </>
        );
      })()}
    </svg>
    <TrendTip tip={tip} />
    </div>
  );
};

RangeChart.propTypes = {
  entries: PropTypes.array.isRequired,
  unit: PropTypes.string,
};

export default RangeChart;
